import { useLocalObservable } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import { DeepLinkType } from '@gem/models/deeplinksModel';
import { useTranslation } from '@gem/modules/i18n';
import { isAndroidSystem, isIOSSystem } from '@gem/utils';

import {
  IUseDeepLinkParamsData,
  useDeepLinkParams,
} from 'APP/main/Modules/DeepLinkHandler/DeepLinkHandler.hooks';
import { RouterParams } from 'APP/router/constants';

interface IDeepLinkViewPresenter {
  deepLinkType: DeepLinkType;
  title: string;
  subtitle: string;
  getParams: IUseDeepLinkParamsData;
  isMobileAppInstalled: boolean;
  isTryToRedirect: boolean;
  rawGetParams: string;
  nativeAppLink: string;
  isMobileClient: boolean;
  appLink: string;
  isExpired: boolean;
  isInvalid: boolean;
  isError: boolean;
  setGetParameters(): void;
  tryRedirectToNativeApp(): void;
}

const CHECK_REDIRECT_TO_NATIVE_APP_TIMEOUT = 1000;

export function useDeepLinkViewPresenter(): IDeepLinkViewPresenter {
  const { getParams } = useDeepLinkParams();
  const { t } = useTranslation();
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const presenter = useLocalObservable<IDeepLinkViewPresenter>(() => {
    return {
      rawGetParams: '',
      getParams,
      isMobileAppInstalled: true,
      isTryToRedirect: false,

      get deepLinkType(): DeepLinkType {
        return presenter.getParams.type;
      },

      get subtitle(): string {
        switch (presenter.deepLinkType) {
          case DeepLinkType.Room:
            return t('deeplink_subtitle_call');
          case DeepLinkType.Group:
            return t('deeplink_subtitle_chat');
          case DeepLinkType.Unknown:
          default:
            return '';
        }
      },

      get title(): string {
        return PROJECT_CONFIG.projectName;
      },

      get nativeAppLink(): string {
        const mobileAppProtocol = `${PROJECT_CONFIG.appLinkProtocol}://`;

        if (!this.isMobileClient) {
          return `${mobileAppProtocol}${window.location.host}${presenter.rawGetParams}`;
        }

        const searchParams = new URLSearchParams(presenter.rawGetParams);
        searchParams.delete(RouterParams.channelId);
        searchParams.delete(RouterParams.entityId);
        searchParams.delete(RouterParams.type);
        searchParams.delete(RouterParams.deepLink);

        const appLinkSearchQuery = searchParams.size ? `?${searchParams.toString()}` : '';

        switch (presenter.deepLinkType) {
          case DeepLinkType.Room:
            return `${mobileAppProtocol}room/${presenter.getParams.channelId}${appLinkSearchQuery}`;
          case DeepLinkType.Group:
            return `${mobileAppProtocol}chat/${presenter.getParams.entityId}${appLinkSearchQuery}`;
          default:
            return '';
        }
      },

      get isMobileClient(): boolean {
        return isIOSSystem() || isAndroidSystem();
      },

      get appLink(): string {
        if (presenter.isMobileClient && presenter.isMobileAppInstalled) {
          return presenter.nativeAppLink;
        }
        return `${window.location.origin}${presenter.rawGetParams}`;
      },

      get isExpired(): boolean {
        return presenter.getParams.expired;
      },

      get isInvalid(): boolean {
        return (
          ![DeepLinkType.Room, DeepLinkType.Group].includes(presenter.deepLinkType) ||
          !presenter.appLink
        );
      },

      get isError(): boolean {
        return presenter.isInvalid || presenter.isExpired;
      },

      setGetParameters(): void {
        presenter.getParams = getParams;

        if (window.location.search) {
          presenter.rawGetParams = window.location.search;
        }
      },

      tryRedirectToNativeApp(): void {
        if (presenter.isError) {
          return;
        }
        presenter.isTryToRedirect = true;
        window.location.href = presenter.nativeAppLink;

        redirectTimer.current = setTimeout(() => {
          if (document.hasFocus()) {
            presenter.isMobileAppInstalled = false;
          }
          presenter.isTryToRedirect = false;
        }, CHECK_REDIRECT_TO_NATIVE_APP_TIMEOUT);
      },
    };
  });

  useEffect(() => {
    if (getParams.isDeepLink) {
      presenter.setGetParameters();
    }
  }, [getParams]);

  useEffect(() => {
    presenter.tryRedirectToNativeApp();

    return () => {
      if (!redirectTimer?.current) {
        return;
      }
      clearTimeout(redirectTimer.current);
    };
  }, []);

  return presenter;
}
